import { useEffect, useState } from 'react';
import { QUARTER } from '../shared/q3';

const Q_END = new Date(QUARTER.end).getTime();

function timeLeft() {
  const left = Math.max(Q_END - Date.now(), 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    days: String(Math.floor(left / 864e5)),
    hours: pad(Math.floor(left / 36e5) % 24),
    mins: pad(Math.floor(left / 6e4) % 60),
    secs: pad(Math.floor(left / 1e3) % 60),
  };
}

/**
 * Ticks on each whole second. Kept in its own component so the per-second
 * re-render touches only the countdown, not the leaderboard around it.
 */
function useTimeLeft() {
  const [t, setT] = useState(timeLeft);
  useEffect(() => {
    let id = 0;
    const tick = () => {
      setT(timeLeft());
      id = window.setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
    };
    id = window.setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
    // Background tabs throttle timers; resync as soon as the tab is visible again.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(id);
      tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  return t;
}

/** Each digit is keyed by its value, so only digits that change remount and play the flip. */
function Flip({ value }: { value: string }) {
  return (
    <span className="sk-flip" aria-hidden="true">
      {value.split('').map((ch, i) => (
        <span key={`${value.length - i}:${ch}`} className="sk-flip-d">
          {ch}
        </span>
      ))}
    </span>
  );
}

export default function Countdown() {
  const t = useTimeLeft();
  const cells: Array<[string, string]> = [
    [t.days, 'Days'],
    [t.hours, 'Hrs'],
    [t.mins, 'Mins'],
    [t.secs, 'Secs'],
  ];
  return (
    <div
      className="sk-cdbar sk-cdhero"
      role="timer"
      aria-label={`Q3 ends in ${t.days} days ${t.hours} hours ${t.mins} minutes`}
    >
      <div className="ttl">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4M7 14h2M11 14h2M15 14h2M7 17h2M11 17h2" />
        </svg>
        <span>
          Q3 ends
          <br />
          in
        </span>
      </div>
      {cells.map(([v, l]) => (
        <div key={l}>
          <div className="v">
            <Flip value={v} />
          </div>
          <div className="l">{l}</div>
        </div>
      ))}
    </div>
  );
}
